/**
 * KID-5 — the arrival-reason step.
 *
 * **There is no fixed count and no fixed order — decision 0198, approved 2026-09-10.** It supersedes 0127's
 * SIX and 0069's FIVE, and it rejects the question rather than answering it: *"there are buttons that do not
 * exist in some use cases"* [Operator]. The seven below are the size of the DECLARED set; what a candidate
 * sees is that set minus whatever their record removes. KID-5's Definition of Done still says "exactly five
 * options, hard-coded" and is owed the amendment 0198 names — ⛔ and the fix there is to DROP the assertion,
 * not to raise the number to six or seven.
 *
 * ⚠️ **The Figma frame draws SIX of them, and that is a design item rather than a count.** On
 * `12 · What brings you in today?` (`12195:11508` desktop / `12195:11555` iPad / `12195:11602` mobile) the
 * seventh — `Reconfirm my assignment information` — is drawn `visible: false`, beside a hidden
 * `Subtitle · active associate`. A hidden frame layer is how the design expresses a CONDITION; it is not
 * evidence the option is gone [0198]. Figma governs how a screen LOOKS, never which capabilities exist —
 * reading it the other way is what deleted the pay card on 2026-09-02, one day after 0127 approved it.
 *
 * ⚠️ **The list is NOT static, and KID-5 still says it must be.** `ArrivalReasonScreen` filters two options
 * against the candidate's record. Decision 0162 (approved 2026-09-03) sanctions a dynamic menu; KID-5's
 * STATIC guard and 0069 were never amended to match. That contradiction is open in the Ground Truth and is
 * not resolved here.
 */
export const ARRIVAL_REASON = {
  reconfirmAssignment: 'reconfirmAssignment',
  /*
   * **«See what I applied for» — the option a candidate with a pending submission gets** [Operator,
   * 2026-09-08]. It is the prototype's `hubAppStatus`, shown on `!hasExistingJob`.
   *
   * It shares its destination with `reconfirmAssignment`: both land on the assignment card, which
   * `03-us-kas-1` already specifies in two variants — "next shift" on a placement, "first day" on a
   * submission. Adding this option is what finally makes the submission variant reachable; before it, the
   * api returned that card and no path in the UI led to it.
   */
  seeMyApplications: 'seeMyApplications',
  applyForJob: 'applyForJob',
  onboardingPaperwork: 'onboardingPaperwork',
  drugTest: 'drugTest',
  /*
   * `payCard` is on the menu, unconditionally — the one entry 0198 re-confirms rather than re-opens
   * (*"el menu de paycard sigue estando"*, Operator 2026-09-10). It is **not** "the sixth": 0198 voids
   * 0127's count while keeping its pay-card ruling, so this option has a place on the menu and no number.
   *
   * ⚠️ It was removed on 2026-09-02 (`ef61fb2`) because the live frame — 12195:11508 and its two siblings —
   * drew five and the pay card was not among them. That was a regression: decision 0127 had ruled the
   * previous day, on ISS's own consensus that the kiosk keeps handing pay cards to returning associates,
   * that the app shipping it *"is already correct"*. Restored 2026-09-07, and the frame has since regained
   * `Link pay card` at all three widths — read from the live file on 2026-09-10.
   *
   * The frame under-draws. That is a Figma defect to raise, not a capability to delete — the rule that
   * Figma governs the design does not extend to deciding which capabilities exist.
   */
  payCard: 'payCard',
  somethingElse: 'somethingElse',
} as const;
export type ArrivalReason = (typeof ARRIVAL_REASON)[keyof typeof ARRIVAL_REASON];

/**
 * The glyphs the frame draws in the option rows' circled icons.
 *
 * Six for seven options — the frame gives the same file-with-a-folded-corner to
 * `reconfirmAssignment` and to `onboardingPaperwork`. The paths, and the per-icon bounding boxes they
 * reproduce, are in `components/ArrivalOptionIcon.tsx`; the names live here so that file exports a
 * component and nothing else (`react-refresh/only-export-components`).
 */
export const ARRIVAL_ICON = {
  search: 'search',
  file: 'file',
  clock: 'clock',
  bubble: 'bubble',
  card: 'card',
  flask: 'flask',
} as const;
export type ArrivalIcon = (typeof ARRIVAL_ICON)[keyof typeof ARRIVAL_ICON];

/**
 * What the candidate's record holds, reduced server-side to the one axis the menu composes from
 * [KID-5a § Handover contract; decision 0387]. **The rules over it are KID-5b's**; this is only the value set.
 *
 * `unknown` is *the read failed*. It is a fourth state, not a fourth kind of record, and it hides nothing
 * [KID-5b's fail-open].
 */
export const HELD_STATE = {
  placement: 'placement',
  submission: 'submission',
  none: 'none',
  unknown: 'unknown',
} as const;
export type HeldState = (typeof HELD_STATE)[keyof typeof HELD_STATE];

/**
 * Whether the I-9 step still applies, reduced server-side [decision 0387]. **The rule over it is KID-5c's**:
 * only `complete` hides the row. ISS's raw compliance value never crosses the wire to this screen, because
 * a lobby screen that rendered it would disclose it to the room.
 */
export const I9_STATE = {
  complete: 'complete',
  outstanding: 'outstanding',
  unknown: 'unknown',
} as const;
export type I9State = (typeof I9_STATE)[keyof typeof I9_STATE];

/**
 * **Everything the arrival menu reads, and nothing else** — the answer of
 * `GET /arrival-reason/:candidateId/menu-state`, exactly `{ held, i9 }` [decision 0387 clause 1]. A build
 * that reads anything more to compose the menu has made a second diagnosis [KID-5a § Handover contract].
 */
export interface ArrivalMenuState {
  readonly held: HeldState;
  readonly i9: I9State;
}

/**
 * **Nothing learned, nothing hidden** — the one fail-open value for the menu's read. Every failure of the
 * read, and a candidate with no id to read, composes the menu from this, which hides no row [KID-5b; KID-5c].
 */
export const ARRIVAL_MENU_UNREAD: ArrivalMenuState = {
  held: HELD_STATE.unknown,
  i9: I9_STATE.unknown,
};

/** What the candidate picked, once they have picked. */
export interface ArrivalSelection {
  readonly reason: ArrivalReason;
  /**
   * @deprecated RETIRED, and always `false`. Read by nothing; due for deletion with its one writer.
   *
   * It recorded whether the selection produced an admin-queue entry, back when selecting a reason did that.
   * Nothing on this menu queues any more [decision 0206; the Operator, 2026-09-11], so the field has one
   * value and no meaning, and it is optional so that a new caller cannot be asked to supply one.
   */
  readonly queued?: boolean;
}

/**
 * The reasons that raise a queue entry when they are SELECTED.
 *
 * It is empty, and the emptiness is the rule rather than a placeholder [decision 0206] — written as a set
 * so that the claim reads as *none of them* rather than as a predicate somebody assumes is broken.
 */
const QUEUEING_REASONS: readonly ArrivalReason[] = [];

/**
 * @deprecated RETIRED. There is no rule from an arrival reason to a queue entry.
 *
 * ⛔ **Delete this, together with the two lines in `ArrivalReasonScreen.tsx` that call it** — the import on
 * line 24 and the `queued:` argument to `setArrival`. It is kept for one change only because that component
 * is being edited on another branch in parallel and a collision there would be more expensive than this
 * tombstone. **Nothing new may call it.**
 *
 * The Operator [2026-09-11]: *"no hay que encolarlos al aplicar a un job, solo si piden asistencia por
 * alguna razón explícita en los botones que estén diseñados para eso, si necesitan completar el i-9 o el
 * drug test. Nada más."* Asked to confirm the reading, he did: **the queue entry belongs to the BUTTON,
 * never to the menu selection.** So a predicate on the reason cannot express the rule — the answer depends
 * on a control the candidate has not reached yet. Choosing *Complete my I-9* raises nothing; tapping
 * *Yes, help me today* on the screen it leads to raises one entry, through `summonRepresentative` or the
 * I-9 notice [decision 0206, amending 0069].
 *
 * ⚠️ **The backend is the authority and has already deleted its copy.** `iss-kiosk-api`'s arrival module
 * queues nobody at all now, so this returning anything but `false` would be the kiosk claiming something
 * the service will not do.
 *
 * Complexity: O(1).
 */
export function queuesForReason(reason: ArrivalReason): boolean {
  return QUEUEING_REASONS.includes(reason);
}
